import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { Metadata, RunView } from '@memberjunction/core';
import { UUIDsEqual } from '@memberjunction/global';
import { Subject } from 'rxjs';
import { IzzyAssignmentRuleEntity, IzzyTicketTeamEntity } from 'mj_generatedentities';
import { IAdminPanel, AdminPanelStatus } from '../admin-panel.interface';

type Priority = 'Low' | 'Medium' | 'High' | 'Critical';

/** Field paths a condition can match against — kept in sync with AssignmentRuleService.resolveField. */
const CONDITION_FIELDS = [
  { value: 'issue.title', label: 'Ticket title' },
  { value: 'issue.description', label: 'Ticket description' },
  { value: 'issue.type', label: 'Ticket type' },
  { value: 'issue.priority', label: 'Ticket priority' },
  { value: 'issue.severity', label: 'Ticket severity' },
  { value: 'issue.requester.organization.planLevel', label: 'Requester plan level' },
] as const;

const CONDITION_OPS = [
  { value: 'equals', label: 'equals' },
  { value: 'notEquals', label: 'does not equal' },
  { value: 'contains', label: 'contains' },
  { value: 'in', label: 'is one of (comma-separated)' },
  { value: '>=', label: '≥' },
  { value: '<=', label: '≤' },
  { value: '>', label: '>' },
  { value: '<', label: '<' },
] as const;

/** One condition row in the builder. */
interface ConditionRow {
  field: string;
  op: string;
  value: string;
}

/** The editable form for a rule (structured view over its Conditions/Actions JSON). */
interface RuleForm {
  name: string;
  description: string;
  priority: number;
  isActive: boolean;
  operator: 'AND' | 'OR';
  conditions: ConditionRow[];
  assignTeamId: string;
  setPriority: '' | Priority;
}

/** A rule plus inline edit state. */
interface RuleDisplay {
  rule: IzzyAssignmentRuleEntity;
  isEditing: boolean;
  form: RuleForm;
}

/**
 * Assignment Rules admin panel (PRD v4.0 §9.3). Org-scoped management of AssignmentRule rows — the
 * routing rules the AssignmentRuleService evaluates (in Priority order) when a ticket is created:
 * a matched rule can set the ticket's priority and/or pick the team the auto-assign mode then assigns
 * within. Admin configuration, so it lives under Administration.
 *
 * The component presents the rule's Conditions/Actions JSON as a structured builder (a list of
 * field/op/value rows + AND/OR, and team/priority action pickers) and serializes to the JSON shape the
 * engine expects on save.
 */
@Component({
  standalone: false,
  selector: 'izzy-assignment-rules',
  templateUrl: './assignment-rules.component.html',
  styleUrls: ['./assignment-rules.component.css'],
})
export class AssignmentRulesComponent implements OnInit, OnDestroy, IAdminPanel {
  @Input() organizationId: string | undefined;
  @Output() statusChange = new EventEmitter<AdminPanelStatus>();

  protected rules: RuleDisplay[] = [];
  protected teams: IzzyTicketTeamEntity[] = [];

  protected readonly fields = CONDITION_FIELDS;
  protected readonly ops = CONDITION_OPS;
  protected readonly priorities: Priority[] = ['Low', 'Medium', 'High', 'Critical'];

  protected isLoading = true;
  protected isSaving = false;
  protected errorMessage = '';
  protected successMessage = '';

  protected isCreating = false;
  protected createForm: RuleForm = this.emptyForm();

  private destroy$ = new Subject<void>();

  async ngOnInit(): Promise<void> {
    await this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private emptyForm(): RuleForm {
    return {
      name: '',
      description: '',
      priority: 100,
      isActive: true,
      operator: 'AND',
      conditions: [{ field: 'issue.type', op: 'equals', value: '' }],
      assignTeamId: '',
      setPriority: '',
    };
  }

  protected async loadData(): Promise<void> {
    if (!this.organizationId) {
      this.rules = [];
      this.isLoading = false;
      return;
    }
    this.isLoading = true;
    this.errorMessage = '';
    try {
      await this.loadTeams();

      const rv = new RunView();
      const res = await rv.RunView<IzzyAssignmentRuleEntity>({
        EntityName: 'Assignment Rules',
        ExtraFilter: `OrganizationID = '${this.organizationId}'`,
        OrderBy: 'Priority ASC, Name ASC',
        ResultType: 'entity_object',
      });
      const rules = res.Success ? res.Results : [];
      this.rules = rules.map(r => ({ rule: r, isEditing: false, form: this.toForm(r) }));

      this.statusChange.emit(
        this.rules.length === 0
          ? { status: 'none', tooltip: 'No assignment rules' }
          : { status: 'good', tooltip: `${this.rules.filter(r => r.rule.IsActive).length} active rule(s)` }
      );
    } catch (error) {
      console.error('AssignmentRules: failed to load', error);
      this.errorMessage = 'Failed to load assignment rules.';
    } finally {
      this.isLoading = false;
    }
  }

  /** Org teams (for the assignTeam picker). */
  private async loadTeams(): Promise<void> {
    const rv = new RunView();
    const res = await rv.RunView<IzzyTicketTeamEntity>({
      EntityName: 'Ticket Teams',
      ExtraFilter: `OrganizationID = '${this.organizationId}' AND Status = 'Active'`,
      OrderBy: 'Name ASC',
      ResultType: 'entity_object',
    });
    this.teams = res.Success ? res.Results : [];
  }

  /** Parse a rule's stored Conditions/Actions JSON into the structured form. */
  private toForm(r: IzzyAssignmentRuleEntity): RuleForm {
    let operator: 'AND' | 'OR' = 'AND';
    let conditions: ConditionRow[] = [];
    try {
      const c = JSON.parse(r.Conditions || '{}');
      operator = (c.operator || 'AND').toUpperCase() === 'OR' ? 'OR' : 'AND';
      conditions = Array.isArray(c.rules)
        ? c.rules.map((leaf: { field?: string; op?: string; value?: unknown }) => ({
            field: leaf.field || 'issue.type',
            op: leaf.op || 'equals',
            value: Array.isArray(leaf.value) ? leaf.value.join(', ') : (leaf.value == null ? '' : String(leaf.value)),
          }))
        : [];
    } catch { /* malformed JSON → empty builder */ }
    if (conditions.length === 0) {
      conditions = [{ field: 'issue.type', op: 'equals', value: '' }];
    }

    let assignTeamId = '';
    let setPriority: '' | Priority = '';
    try {
      const a = JSON.parse(r.Actions || '{}');
      assignTeamId = a.assignTeam || '';
      if (['Low', 'Medium', 'High', 'Critical'].includes(a.setPriority)) {
        setPriority = a.setPriority;
      }
    } catch { /* malformed JSON → no actions */ }

    return {
      name: r.Name,
      description: r.Description || '',
      priority: r.Priority,
      isActive: !!r.IsActive,
      operator,
      conditions,
      assignTeamId,
      setPriority,
    };
  }

  /** Serialize the structured form back to the engine's Conditions/Actions JSON. */
  private buildConditionsJson(form: RuleForm): string {
    const rules = form.conditions
      .filter(c => c.field && c.op)
      .map(c => ({
        field: c.field,
        op: c.op,
        value: c.op === 'in'
          ? c.value.split(',').map(v => v.trim()).filter(Boolean)
          : c.value,
      }));
    return JSON.stringify({ operator: form.operator, rules });
  }

  private buildActionsJson(form: RuleForm): string {
    const actions: { assignTeam?: string; setPriority?: string } = {};
    if (form.assignTeamId) actions.assignTeam = form.assignTeamId;
    if (form.setPriority) actions.setPriority = form.setPriority;
    return JSON.stringify(actions);
  }

  // ========== Display helpers ==========

  teamName(teamId: string | null): string {
    if (!teamId) return '';
    return this.teams.find(t => UUIDsEqual(t.ID, teamId))?.Name || 'Unknown';
  }

  fieldLabel(field: string): string {
    return CONDITION_FIELDS.find(f => f.value === field)?.label || field;
  }

  opLabel(op: string): string {
    return CONDITION_OPS.find(o => o.value === op)?.label || op;
  }

  /** A short human summary of a rule's actions for the list row. */
  actionsSummary(d: RuleDisplay): string {
    const parts: string[] = [];
    if (d.form.assignTeamId) parts.push(`→ ${this.teamName(d.form.assignTeamId)}`);
    if (d.form.setPriority) parts.push(`priority ${d.form.setPriority}`);
    return parts.join(', ') || '—';
  }

  // ========== Condition rows ==========

  addCondition(form: RuleForm): void {
    form.conditions.push({ field: 'issue.type', op: 'equals', value: '' });
  }

  removeCondition(form: RuleForm, index: number): void {
    form.conditions.splice(index, 1);
    if (form.conditions.length === 0) {
      form.conditions.push({ field: 'issue.type', op: 'equals', value: '' });
    }
  }

  // ========== Create ==========

  startCreate(): void {
    this.isCreating = true;
    this.createForm = this.emptyForm();
  }

  cancelCreate(): void {
    this.isCreating = false;
  }

  async saveCreate(): Promise<void> {
    if (!this.validate(this.createForm)) return;
    if (!this.organizationId) return;
    this.isSaving = true;
    try {
      const md = new Metadata();
      const r = await md.GetEntityObject<IzzyAssignmentRuleEntity>('Assignment Rules');
      r.NewRecord();
      r.OrganizationID = this.organizationId;
      this.applyForm(r, this.createForm);
      if (!await r.Save()) {
        this.errorMessage = r.LatestResult?.CompleteMessage || 'Failed to create rule.';
        return;
      }
      await this.loadData();
      this.isCreating = false;
      this.showSuccess('Assignment rule created');
    } catch (error) {
      console.error('AssignmentRules: create failed', error);
      this.errorMessage = 'Failed to create rule.';
    } finally {
      this.isSaving = false;
    }
  }

  // ========== Edit ==========

  startEdit(d: RuleDisplay): void {
    d.isEditing = true;
    d.form = this.toForm(d.rule);
  }

  cancelEdit(d: RuleDisplay): void {
    d.isEditing = false;
  }

  async saveEdit(d: RuleDisplay): Promise<void> {
    if (!this.validate(d.form)) return;
    this.isSaving = true;
    try {
      this.applyForm(d.rule, d.form);
      if (!await d.rule.Save()) {
        this.errorMessage = d.rule.LatestResult?.CompleteMessage || 'Failed to update rule.';
        return;
      }
      d.isEditing = false;
      this.showSuccess('Assignment rule updated');
    } catch (error) {
      console.error('AssignmentRules: edit failed', error);
      this.errorMessage = 'Failed to update rule.';
    } finally {
      this.isSaving = false;
    }
  }

  async confirmDelete(d: RuleDisplay): Promise<void> {
    this.isSaving = true;
    try {
      if (!await d.rule.Delete()) {
        this.errorMessage = 'Failed to delete rule.';
        return;
      }
      this.rules = this.rules.filter(x => x !== d);
      this.showSuccess('Assignment rule deleted');
    } catch (error) {
      console.error('AssignmentRules: delete failed', error);
      this.errorMessage = 'Failed to delete rule.';
    } finally {
      this.isSaving = false;
    }
  }

  // ========== Shared ==========

  private validate(form: RuleForm): boolean {
    if (!form.name.trim()) {
      this.errorMessage = 'Rule name is required.';
      return false;
    }
    const realConditions = form.conditions.filter(c => c.field && c.op && c.value.trim());
    if (realConditions.length === 0) {
      this.errorMessage = 'Add at least one condition with a value.';
      return false;
    }
    if (!form.assignTeamId && !form.setPriority) {
      this.errorMessage = 'Set at least one action (assign a team or set priority).';
      return false;
    }
    this.errorMessage = '';
    return true;
  }

  private applyForm(r: IzzyAssignmentRuleEntity, form: RuleForm): void {
    r.Name = form.name.trim();
    r.Description = form.description.trim() || null;
    r.Priority = form.priority ?? 100;
    r.IsActive = form.isActive;
    r.Conditions = this.buildConditionsJson(form);
    r.Actions = this.buildActionsJson(form);
  }

  private showSuccess(message: string): void {
    this.successMessage = message;
    setTimeout(() => { this.successMessage = ''; }, 3000);
  }
}

export function LoadAssignmentRulesComponent(): void {}
